import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useRecoilValue } from "recoil";
import {
  imagesBlobState,
  selectedShippingAddress,
  imageFrameState,
  validCoupon,
  imagesLoadingState,
} from "@atoms";
import {
  netPriceState,
  imageCountState,
  framePricesState,
  defaultFramePriceState,
  shippingPriceState,
  handlingFeeState,
  creditsAppliedState,
} from "@atoms/priceCalc";
import AmountCharged from "../AmountCharged";
import { paymentErrorsConfig } from "../PaymentErrors";
import { OrderStatus } from "../../constants/enums/orderStatus";
import { rememberOrderAccess } from "../utils/orderAccess";

/**
 * One-press card checkout for PHOTO orders (gift cards have their own path).
 *
 * payAndOrder(values) runs, in order:
 *   1. POST /payment/do { verifyFirst: true } — the server runs a J2 card check
 *      (tranmode N, no hold) and, ONLY if Tranzila answers 000, the real charge.
 *   2. Records the order: /user/createorder for a fresh cart, or
 *      /user/changeorderstatus when a wallet/Bit preload already opened a
 *      NOT_PAID order for this cart.
 *   3. Clears local storage and lands on /payment-success/:oid.
 *
 * If images are still uploading the submit is parked and fires by itself the
 * moment uploads finish (same gate the checkout drawer used to apply).
 * The amount is the checkout drawer amount: coupon AND Moments+ points applied.
 */

const BASE_URL = process.env.REACT_APP_BASE_URL;
const FREE_SHIPPING_FROM = 5;

export const ORDER_AFTER_CHARGE_ERROR =
  "התשלום התקבל, אבל ההזמנה לא נרשמה. אין צורך לשלם שוב - צרו איתנו קשר ונסדיר את זה מיד.";
const GENERIC_CARD_ERROR = "תקלה בכרטיס האשראי";
const REQUEST_FAILED_ERROR = "שגיאה בביצוע התשלום, נסו שוב";

export const getCardExpDate = (month, year) => {
  const shortYear = year % 100;
  const shortYearStr = shortYear < 10 ? "0" + shortYear.toString() : shortYear.toString();
  return `${month}${shortYearStr}`;
};

const describeCardError = (data) => {
  const code = data && data.errorCode;
  if (code && paymentErrorsConfig[code]) return paymentErrorsConfig[code].error;
  return GENERIC_CARD_ERROR;
};

export default function useCardOrderPayment() {
  const navigate = useNavigate();
  const imageCount = useRecoilValue(imageCountState);
  const imagesBlob = useRecoilValue(imagesBlobState);
  const netPrice = useRecoilValue(netPriceState);
  const framePrices = useRecoilValue(framePricesState);
  const defaultFramePrice = useRecoilValue(defaultFramePriceState);
  const shippingPrice = useRecoilValue(shippingPriceState);
  const handlingFee = useRecoilValue(handlingFeeState);
  const creditsApplied = useRecoilValue(creditsAppliedState);
  const selectedCoupon = useRecoilValue(validCoupon);
  const selectedAddress = useRecoilValue(selectedShippingAddress);
  const frameChoose = useRecoilValue(imageFrameState);
  const isImagesLoading = useRecoilValue(imagesLoadingState);

  const currentFrameType = frameChoose || localStorage.getItem("frameSelected") || "classic";
  const framePrice = framePrices[currentFrameType] || defaultFramePrice;
  const totalImages = imageCount + imagesBlob.length;
  const amountCharged = AmountCharged(
    totalImages,
    FREE_SHIPPING_FROM,
    netPrice + imagesBlob.length * framePrice,
    shippingPrice,
    selectedCoupon,
    handlingFee,
    creditsApplied
  );

  const [isPaying, setIsPaying] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  // A submit parked until uploads finish: { values, beforeNavigate }.
  const [pending, setPending] = useState(null);
  const inFlight = useRef(false);

  const finish = (oid, beforeNavigate) => {
    if (beforeNavigate) beforeNavigate();
    localStorage.clear();
    navigate(`/payment-success/${oid}`);
  };

  const run = async (values, beforeNavigate) => {
    if (inFlight.current) return { ok: false, reason: "busy" };
    inFlight.current = true;
    setIsPaying(true);
    setPaymentError("");
    try {
      const card = {
        no: values.cardNumber.replace(/\s/g, ""),
        expdate: getCardExpDate(values.expiriedMonth, values.expiriedYear),
        cvv: values.cvv,
        email: (selectedAddress && selectedAddress.email) || "",
        id: values.id,
      };

      // finish() clears localStorage; the success page still needs these ids.
      rememberOrderAccess();

      // 1. A wallet preload or an abandoned Bit attempt may have left an order
      //    behind. Reuse it only while it is still NOT_PAID.
      let existingOrderId = localStorage.getItem("orderId");
      if (existingOrderId) {
        let status = null;
        try {
          const check = await axios.get(`${BASE_URL}/user/checkorder/${existingOrderId}`);
          status = check.data && check.data.orderStatus;
        } catch (e) {
          status = null;
        }
        if (status === OrderStatus.PAID) {
          // Already paid (success page never reached) — never charge twice.
          finish(existingOrderId, beforeNavigate);
          return { ok: true, oid: existingOrderId, alreadyPaid: true };
        }
        if (status !== OrderStatus.NOT_PAID) {
          localStorage.removeItem("orderId");
          existingOrderId = null;
        }
      }

      // 2. J2 check + charge in one server call (the charge only runs on 000).
      const pay = await axios.post(`${BASE_URL}/payment/do`, {
        card,
        amount: amountCharged,
        verifyFirst: true,
      });
      if (!pay.data || pay.data.status !== 200) {
        setPaymentError(describeCardError(pay.data));
        return { ok: false, stage: (pay.data && pay.data.stage) || "charge" };
      }
      const last4Digits = pay.data.last4Digits;

      // 3. Record the order. The card IS charged from here on: never retry the charge.
      let oid;
      try {
        if (existingOrderId) {
          const r = await axios.post(`${BASE_URL}/user/changeorderstatus/${existingOrderId}`, {
            status: OrderStatus.PAID,
            amountCharged,
            last4Digits,
            paymentMethod: "card",
          });
          if (!r.data || r.data.status === 400) throw new Error("changeorderstatus failed");
          oid = existingOrderId;
        } else {
          const r = await axios.post(
            `${BASE_URL}/user/createorder`,
            {
              ...selectedAddress,
              uid: localStorage.getItem("uniqueUserId"),
              sessionId: localStorage.getItem("sessionId"),
              finalAmount: amountCharged,
              orderStatus: OrderStatus.PAID,
              frameType: currentFrameType,
              imagesPayed: totalImages,
              last4Digits,
            },
            { headers: { "Content-Type": "application/json" } }
          );
          if (!r.data || !r.data.oid) throw new Error("createorder returned no oid");
          oid = r.data.oid;
        }
      } catch (orderErr) {
        console.error(
          "[card checkout] CHARGED but order not recorded",
          {
            amountCharged,
            last4Digits,
            existingOrderId,
            sessionId: localStorage.getItem("sessionId"),
            uid: localStorage.getItem("uniqueUserId"),
          },
          orderErr
        );
        setPaymentError(ORDER_AFTER_CHARGE_ERROR);
        return { ok: false, charged: true };
      }

      finish(oid, beforeNavigate);
      return { ok: true, oid };
    } catch (err) {
      console.error("[card checkout] payment request failed", err);
      setPaymentError(REQUEST_FAILED_ERROR);
      return { ok: false };
    } finally {
      inFlight.current = false;
      setIsPaying(false);
    }
  };

  // A parked submit fires once uploads are done (latest amount/address at that moment).
  useEffect(() => {
    if (!pending || isImagesLoading) return;
    const { values, beforeNavigate } = pending;
    setPending(null);
    run(values, beforeNavigate);
  }, [isImagesLoading, pending]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * @param values formik card values (cardNumber, expiriedMonth, expiriedYear, cvv, id)
   * @param opts.beforeNavigate called right before leaving for the success page (close the modal)
   * @returns {{ok: boolean}} resolves when the attempt ends, or at once ({deferred: true}) if parked
   */
  const payAndOrder = (values, { beforeNavigate } = {}) => {
    if (isImagesLoading) {
      setPaymentError("");
      setPending({ values, beforeNavigate });
      return Promise.resolve({ ok: false, deferred: true });
    }
    return run(values, beforeNavigate);
  };

  // Drop a parked submit (user closed the form while uploads were still running).
  const cancelPending = () => setPending(null);

  return {
    payAndOrder,
    cancelPending,
    isPaying: isPaying || !!pending,
    paymentError,
    amountCharged,
  };
}
